/**
 * 会话历史摘要（primeText）—— 新建引擎会话时注入的"此前聊过什么"。
 *
 * ## 它在体系中的位置（重要）
 *
 * 首选机制是**引擎原生持久化**：`ctx.sessionPersistence` 让
 * `agents.create({ sessionId })` 自动 resume，恢复的是引擎自己写的完整内部
 * transcript（工具调用配对、step/turn 边界全是原件）。那条路径**不需要**本文件。
 *
 * 本文件是**兜底**：当存档不存在或恢复失败（数据库被清、格式不兼容、
 * 持久化未挂载）时，会话只能新建 —— 新建时必须把历史"讲"给模型听。
 *
 * ## 历史教训：这份摘要曾经是有损的
 *
 * 旧实现有两处造成"上下文缺中间记录"的缺陷：
 * 1. 只取 `m.content` —— assistant 的工具调用消息 content 为空，
 *    于是**工具参数被整体丢弃**（"把第七章第一段改成测试"这类操作的写入内容
 *    只存在于 tool_calls 里）；工具结果也完全没进来；
 * 2. 总长截断取"头部"6000 字，后来又是 8000 字取头尾、**中间整段丢弃** ——
 *    长对话后段的操作记录被整段删掉。
 *
 * 这两处叠加的结果，就是生产上观察到的"AI 说它只看到三条回答、开场白断在
 * `tar`"。**任何字符级预算都是缺陷，不是优化** —— 它造成的缺损不可恢复，
 * 而且模型看不出自己少了东西，只会"自信地答偏"。
 *
 * ## 现在的实现
 *
 * - **不做任何截断**：用户消息、AI 回答、工具调用参数、工具结果全文保留；
 * - `includeTools` 控制是否带上工具链路（默认带 —— 工具链路正是模型
 *   判断"我已经做过什么"的依据）；
 * - 长度不受任何常量限制。真遇到模型上下文窗口装不下时，应该**明确告知模型
 *   "历史被截断到某处"**，而不是让它自己猜（那正是这次事故里消耗了两轮对话的
 *   东西：模型反复怀疑"上下文丢失"）。
 */

import type { AIMessage } from '../types';

const HEADER = '【以下是本次对话的完整历史记录，供背景参考（你的当前消息在后面）：】';

export interface BuildHistoryPrimeOptions {
  /** 是否包含工具调用与结果。默认 true —— 关掉会丢掉"我做过什么"的依据。 */
  includeTools?: boolean;
}

/**
 * 把 UI 侧消息历史整理为模型可读的纯文本。
 *
 * 刻意**不设置任何长度预算**：历史必须完整传达。若极端情况下确实超出模型窗口，
 * 应在调用处显式判断并告知模型，而不是在这里悄悄砍掉中间部分。
 */
export function buildHistoryPrime(history: AIMessage[], options: BuildHistoryPrimeOptions = {}): string {
  if (history.length === 0) return '';

  const includeTools = options.includeTools !== false;
  const lines: string[] = [];

  for (const m of history) {
    if (m.role === 'user') {
      const text = (m.content ?? '').trim();
      if (text) lines.push(`用户：${text}`);
      continue;
    }

    if (m.role === 'tool') {
      if (!includeTools) continue;
      const text = (m.content ?? '').trim();
      const callId = m.tool_call_id ? `（call ${m.tool_call_id}）` : '';
      if (text) lines.push(`工具结果${callId}：${text}`);
      continue;
    }

    // assistant：可能是纯回答，也可能是带工具调用的步骤
    const text = (m.content ?? '').trim();
    const calls = includeTools
      ? (m.tool_calls ?? [])
        // 工具参数是"AI 当时到底提交了什么"的唯一凭据，原样保留
        .map((tc) => `→ 调用 ${tc.function.name}：${tc.function.arguments}`)
        .join('\n')
      : '';

    if (calls) {
      lines.push(text ? `AI（执行工具）：${text}\n${calls}` : `AI（执行工具）\n${calls}`);
    } else if (text) {
      lines.push(`AI：${text}`);
    }
  }

  if (lines.length === 0) return '';
  return `${HEADER}\n${lines.join('\n\n')}`;
}
